/**
 * React Context for Babylon.js integration
 */

import { createContext, useContext } from "react";
import type { BabylonContextValue } from "./types";

/**
 * Default context value
 */
const defaultContextValue: BabylonContextValue = {
  error: null,
  isInitialized: false,
  isInitializing: false,
  manager: null,
};

/**
 * Babylon Context for providing manager instance
 */
export const BabylonContext =
  createContext<BabylonContextValue>(defaultContextValue);

/**
 * Display name for debugging
 */
BabylonContext.displayName = "BabylonContext";

/**
 * Context consumer for class components
 */
export const BabylonConsumer = BabylonContext.Consumer;

/**
 * Hook to use Babylon context
 * @throws Error if used outside of BabylonProvider
 */
export function useBabylonContext(): BabylonContextValue {
  const context = useContext(BabylonContext);

  // Check if we're using the default context (not wrapped in provider)
  if (context === defaultContextValue) {
    throw new Error("useBabylonContext must be used within a BabylonProvider");
  }

  return context;
}
